import { CardShell } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { ArrowLeft, Sparkles } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { useCreateArticle } from '@/hooks/useArticles'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { apiErrorMessage } from '@/lib/api-error'
import { readComposerSeed } from '@/lib/article-composer-link'
import { ArticleComposer } from './ArticleComposer'
import { applyBrandTemplate, isBodyEmpty, seededArticleForm } from './brand-template'
import { discardRemovedImages } from './discard-images'
import { reconcileImageFileIds, toCreateInput } from './form-model'
import { useArticleComposerState } from './useArticleComposerState'

/**
 * The template offer.
 *
 * Shown only while the body is still empty, so choosing it can never overwrite
 * authored prose — and it disappears once the template is in, because the template
 * is a starting point that is applied at most once.
 */
function BrandTemplateOffer({ onApply }: { onApply: () => void }) {
	return (
		<CardShell className="mb-6 flex flex-wrap items-center gap-3 border-dashed p-0 px-4 py-3">
			<Sparkles className="h-4 w-4 shrink-0 text-ds-text-subtle" />
			<p className="min-w-0 flex-1 text-sm text-ds-text-muted">
				Start from the <span className="text-ds-text">brand template</span> — a Brand voice section and a Brand colors
				palette matrix. Everything it inserts is editable afterwards.
			</p>
			<Button variant="secondary" size="sm" className="rounded-ds-full" onPress={onApply}>
				Use brand template
			</Button>
		</CardShell>
	)
}

/**
 * New article (route `/articles/new`).
 *
 * ONE request: the create body carries the classification tuple, so there is no
 * create-then-classify pair here. Splitting it would reintroduce the failure the
 * API's create guarantee removes — an article that exists but was never tagged,
 * with nothing to tell the author.
 *
 * No status control: an article opens as a draft and moves only through the
 * transition endpoint, which needs an article that exists.
 *
 * The link that opened it may carry a seed - the Brand details tab sends a brand
 * and asks for the template - which is read once, into the form's initial value.
 */
export function NewArticlePage() {
	const navigate = useNavigate()
	const create = useCreateArticle()
	const { axes } = useAxesWithValues()
	const [searchParams] = useSearchParams()
	const { form, set, setForm, imageProps } = useArticleComposerState(seededArticleForm(readComposerSeed(searchParams)))

	const onSubmit = () => {
		// An image dropped and then deleted again before the first save: its upload
		// still confirmed, so the file exists and nothing points at it.
		const { removedFileIds } = reconcileImageFileIds(form)
		create.mutate(toCreateInput(form), {
			onSuccess: (article) => {
				void discardRemovedImages(removedFileIds)
				void navigate(`/articles/${article.articleId}/edit`)
			},
		})
	}

	return (
		<div>
			<Link
				to="/articles"
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Articles
			</Link>
			<PageHeader
				eyebrow="Articles / New"
				title="New article"
				subtitle="Long-form editorial content, classified on the same axes as every other object."
			/>
			{isBodyEmpty(form.content) ? <BrandTemplateOffer onApply={() => setForm(applyBrandTemplate)} /> : null}
			<ArticleComposer
				form={form}
				set={set}
				axes={axes}
				{...imageProps}
				submitLabel="Create article"
				isPending={create.isPending}
				onSubmit={onSubmit}
				onCancel={() => void navigate('/articles')}
				error={create.isError ? apiErrorMessage(create.error, 'Could not create the article.') : null}
			/>
		</div>
	)
}
